import { useMemo, useEffect, useEffectEvent } from 'react';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import { IFilterFormItem, TFilterFormValues } from 'common/types';
import {
    IOfferEventSubscriptionStatus,
    ExtraUserFieldsDocument,
} from 'graphql/types';
import { useExtraFieldsFilters } from 'user/hooks/useExtraFieldsFilters';
import {
    filtersToBase64String,
    filtersToQueryParams,
} from 'common/utils/filter';
import { useUrlFilterParams } from 'common/hooks/useUrlFilterParams';
import { IOfferEventEnrollmentManagementFilterValues } from 'offer/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { getUserExtraFieldsFilterValues } from 'user/utils/filter';
import { getEnrollmentsManagementFilters } from 'offer/utils/filters';

export const useEnrollmentsManagementFilters = (
    enrollmentStatus: IOfferEventSubscriptionStatus
) => {
    const { showApolloError } = useApolloError();
    const { filterParams, setFilterParam } = useUrlFilterParams(
        'enrollmentManagement'
    );
    const {
        data: extraUserFieldsData,
        loading: extraUserFieldsLoading,
        error,
    } = useQuery(ExtraUserFieldsDocument, {});

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const { extraCategoryFilters } = useExtraFieldsFilters(false);

    const filters: IFilterFormItem[] = useMemo(
        () => [
            ...getEnrollmentsManagementFilters(enrollmentStatus),
            ...extraCategoryFilters,
        ],
        [extraCategoryFilters]
    );

    const handleSearch = (searchValue: string) => {
        setFilterParam('q', searchValue);
    };

    const handleFilterChange = (values?: TFilterFormValues) => {
        const filterString = filtersToBase64String(filters, values);

        setFilterParam('filters', filterString);
    };

    const selectedFilterParams = filterParams.filters;

    const selectedFilters: IOfferEventEnrollmentManagementFilterValues =
        useMemo(() => {
            if (!selectedFilterParams) return {};

            try {
                return JSON.parse(window.atob(selectedFilterParams));
            } catch {
                return {};
            }
        }, [selectedFilterParams]);

    const filterValuesForQuery = useMemo(() => {
        const { attendance, ...restFilters } = selectedFilters;

        const filterValues =
            filtersToQueryParams<IOfferEventEnrollmentManagementFilterValues>(
                filters,
                restFilters
            );

        if (attendance) {
            const [attendanceFrom, attendanceTo] = attendance || [];
            filterValues.presenceFrom = !!attendanceFrom
                ? attendanceFrom
                : undefined;
            filterValues.presenceTo = !!attendanceTo ? attendanceTo : undefined;
        }

        const { extraUserFields } = extraUserFieldsData || {};

        const extraFieldsObject = getUserExtraFieldsFilterValues(
            extraUserFields,
            filterValues
        );

        if (!!extraFieldsObject?.length) {
            filterValues.extraFields = extraFieldsObject;
        }

        return filterValues;
    }, [selectedFilters, filters]);

    return {
        loading: extraUserFieldsLoading,
        filters,
        selectedFilters,
        filterValues: { ...selectedFilters, q: filterParams.q },
        filterValuesForQuery: { ...filterValuesForQuery, q: filterParams.q },
        handleSearch,
        handleFilterChange,
    };
};
